export interface EntityQueueCompoundId {
  queueId: string;
  flagId?: string;
  entityId: string;
  stageId?: string;
}

const SEPARATOR = ':';

/** Distinguishes an entity+stage compound from the flag compound of the same segment count.
 *  Never collides with real ids — packed uuids are 22 chars, raw uuids 36. */
const STAGE_MARKER = 'wo';

export class EntityQueueId {

  public static encodeFlag(queueId: string, flagId: string, entityId: string): string {

    return [queueId, flagId, entityId].map(packUuid).join(SEPARATOR);
  }

  public static encodeFlagStage(queueId: string, flagId: string, entityId: string, stageId: string): string {

    return [queueId, flagId, entityId, stageId].map(packUuid).join(SEPARATOR);
  }

  public static encodeEntity(queueId: string, entityId: string): string {

    return [queueId, entityId].map(packUuid).join(SEPARATOR);
  }

  public static encodeEntityStage(queueId: string, entityId: string, stageId: string): string {

    return [packUuid(queueId), STAGE_MARKER, packUuid(entityId), packUuid(stageId)].join(SEPARATOR);
  }

  public static decode(compoundId: string): EntityQueueCompoundId | null {

    if (!compoundId) return null;

    const parts = compoundId.split(SEPARATOR).map(unpackUuid);
    if (parts.some(p => !p)) return null;

    if (parts.length === 4 && parts[1] === STAGE_MARKER) {
      return { queueId: parts[0], entityId: parts[2], stageId: parts[3] };
    }
    if (parts.length === 4) {
      return { queueId: parts[0], flagId: parts[1], entityId: parts[2], stageId: parts[3] };
    }
    if (parts.length === 3) {
      return { queueId: parts[0], flagId: parts[1], entityId: parts[2] };
    }
    if (parts.length === 2) {
      return { queueId: parts[0], entityId: parts[1] };
    }
    return null;
  }
}

// ── UUID <-> base64url encoding ──────────────────────────────────────
// Packs a canonical UUID string (36 chars, with dashes) into a 22-char base64url token.
// Reversible. Hides the UUID format in URLs.

function packUuid(uuid: string): string {

  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) return uuid;

  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytesToBase64Url(bytes);
}

function unpackUuid(token: string): string {

  if (!token || token.length !== 22) return token;

  const bytes = base64UrlToBytes(token);
  if (!bytes || bytes.length !== 16) return token;

  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.substr(0, 8)}-${hex.substr(8, 4)}-${hex.substr(12, 4)}-${hex.substr(16, 4)}-${hex.substr(20, 12)}`;
}

function bytesToBase64Url(bytes: Uint8Array): string {

  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(token: string): Uint8Array | null {

  try {
    const base64 = token.replace(/-/g, '+').replace(/_/g, '/') + '=='.substr(0, (4 - (token.length % 4)) % 4);
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}
